import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE } from '@fitness-force/models';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';

// Data Grid and related imports
import { DataGridComponent } from '@fitness-force/grid';
import { NoComponentComponent } from '@fitness-force/grid';
import { CategoryTagComponent } from '../data-grid/category-tag/category-tag.component';
import { SalesBasisTagComponent } from '../data-grid/sales-basis-tag/sales-basis-tag.component';
import { ScopeTypeTagComponent } from '../data-grid/scope-type-tag/scope-type-tag.component';
import { ScopeNameComponent } from '../data-grid/scope-name/scope-name.component';
import { StatusTagComponent } from '../data-grid/status-tag/status-tag.component';
import { ActionsComponent } from '@fitness-force/grid-utility/action';

// Models and services
import { ToastMessageService } from '@fitness-force/api';
import { ConfirmationService } from 'primeng/api';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { CommissionPlanRuleResponse, ListCommissionPlanRulesResponse, CommissionPlanResponse, OwnershipLevel } from '../../models';
import { CommissionPlanRuleService, CommissionPlanService } from '../../services';

@Component({
  selector: 'lib-commission-plan-rules-list',
  standalone: true,
  imports: [
    FormsModule,
    TranslateModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    DialogModule,
    BreadcrumbComponent,
    DataGridComponent
],
  providers: [ConfirmationService],
  template: `
  <div class="pt-3 md:px-4 px-2 pb-8 safe-area-margin">
    <h1 class="text-base md:text-lg text-900 flex align-items-center mb-0 line-height-1">
      {{ 'COMMISSION_MANAGEMENT.RULES.TITLE' | translate }}
    </h1>
    <div class="mt-1 mb-3">
      <app-breadcrumb />
    </div>
  
    <div class="surface-0 border-round shadow-1 mt-3">
      <div class="px-2 md:px-3 pt-3">
        <div class="flex justify-content-between align-items-center mb-3">
          <div>
            @if (planName) {
              <p class="text-500 mt-0">{{ 'COMMISSION_MANAGEMENT.RULES.SUBTITLE' | translate: {planName: planName} }}</p>
            }
          </div>
          <div class="flex gap-2">
            <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.BACK' | translate"
              icon="pi pi-arrow-left"
              [text]="true"
              (onClick)="goBack()"
              aria-label="back-button" />
            <p-button [label]="'COMMISSION_MANAGEMENT.RULES.CREATE' | translate"
              icon="pi pi-plus"
              (onClick)="createRule()"
              aria-label="create-commission-rule-button" />
          </div>
        </div>
  
        <!-- Data Grid -->
        <app-data-grid [loading]="loading"
          [columnData]="tableData"
          [columns]="columns"
          [EmptyState]="emptyStateComponent$"
          [totalRecords]="totalRecordLength"
          [paginator]="false"
          [gridName]="'commission-plan-rules-list'"
          [resizableColumns]="true"
          [columnResizeMode]="'expand'"
          [selectable]="selectable"
          [resizableColumns]="true"
          [columnResizeMode]="'expand'"
          (onAction)="onActionEvent($event)"
          data-testid="commission-plan-rules-data-grid" />
      </div>
    </div>
  </div>
  
  <!-- Scope Names Dialog -->
  <p-dialog
    [visible]="showDialog"
    [header]="'COMMISSION_MANAGEMENT.RULES.SCOPE_DETAILS' | translate"
    [modal]="true"
    [style]="{width: '50vw'}"
    [breakpoints]="{'960px': '75vw', '640px': '90vw'}"
    [draggable]="false"
    [resizable]="false">
    <div class="flex flex-column gap-3">
      @if (dialogScopeName) {
        <div>
          <h4 class="text-900 font-semibold mt-0 mb-2">{{ 'COMMISSION_MANAGEMENT.RULES.SCOPE_NAME' | translate }}</h4>
          <div class="surface-50 border-round p-3">
            <ul class="list-none p-0 m-0">
              @for (name of getScopeNamesList(dialogScopeName); track name; let i = $index) {
                <li
                  class="py-2 border-bottom-1 surface-border"
                  [class.border-bottom-none]="i === getScopeNamesList(dialogScopeName).length - 1">
                  <i class="pi pi-check-circle text-green-500 mr-2"></i>
                  <span class="text-900">{{ name }}</span>
                </li>
              }
            </ul>
          </div>
        </div>
      }
  
      @if (dialogSkuName && dialogSkuName !== 'all') {
        <div>
          <h4 class="text-900 font-semibold mt-0 mb-2">{{ 'COMMISSION_MANAGEMENT.RULES.SKU_NAMES' | translate }}</h4>
          <div class="surface-50 border-round p-3">
            <ul class="list-none p-0 m-0">
              @for (sku of getScopeNamesList(dialogSkuName); track sku; let i = $index) {
                <li
                  class="py-2 border-bottom-1 surface-border"
                  [class.border-bottom-none]="i === getScopeNamesList(dialogSkuName).length - 1">
                  <i class="pi pi-tag text-blue-500 mr-2"></i>
                  <span class="text-900">{{ sku }}</span>
                </li>
              }
            </ul>
          </div>
        </div>
      }
    </div>
  
    <ng-template pTemplate="footer">
      <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.CLOSE' | translate"
        (onClick)="showDialog = false"
        [outlined]="true"
        aria-label="close-dialog-button" />
    </ng-template>
  </p-dialog>
  
  <p-confirmDialog />
  <p-toast />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommissionPlanRulesListComponent implements OnInit, OnDestroy {
  private readonly commissionPlanRuleService = inject(CommissionPlanRuleService);
  private readonly commissionPlanService = inject(CommissionPlanService);
  private readonly toastService = inject(ToastMessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly translate = inject(TranslateService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly destroy$ = new Subject<void>();

  rules: CommissionPlanRuleResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  planId!: number;
  ownership!: string;
  planName = '';
  tenantId = 0;
  tenantFranchiseId = 0;
  
  // Data Grid properties
  tableData = new BehaviorSubject<any>([]);
  columns = new BehaviorSubject<any>([]);
  emptyStateComponent$ = new BehaviorSubject<any>(NoComponentComponent);
  totalRecordLength = new BehaviorSubject<any>({
    totalPage: 1,
    totalItems: 0,
    currentPage: 0,
    reset: true,
  });
  
  // Dialog properties
  showDialog = false;
  dialogScopeName = '';
  dialogSkuName = '';
  selectable = new BehaviorSubject<boolean>(false);

  ngOnInit() {
    combineLatest([
      this.store.select(TENANTS_INFO).pipe(filter((t: TENANTSINTERFACE) => !!t?.selected_tenant?.id)),
      this.route.params
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([tenant, params]: [TENANTSINTERFACE, Record<string, string>]) => {
          this.tenantId = tenant.selected_tenant.id;
          this.tenantFranchiseId = tenant.selected_tenant.franchise_group_id;

          if (params['planId']) {
            const newPlanId = +params['planId'];
            const newOwnership = params['ownership'] as OwnershipLevel;
            if (newOwnership && Object.values(OwnershipLevel).includes(newOwnership)) {
              this.ownership = newOwnership;
              this.commissionPlanService.setSelectedOwnershipLevel(newOwnership);
            }
            if (newPlanId !== this.planId) {
              this.planId = newPlanId;
              this.initializeColumns();
              this.loadPlan();
              this.loadRules();
            }
          }
        }
      });
  }

  initializeColumns() {
    this.translate
      .get([
        'COMMISSION_MANAGEMENT.RULES.CATEGORY',
        'COMMISSION_MANAGEMENT.RULES.SALES_BASIS',
        'COMMISSION_MANAGEMENT.RULES.SCOPE_TYPE',
        'COMMISSION_MANAGEMENT.RULES.SCOPE_NAME',
        'COMMISSION_MANAGEMENT.RULES.STATUS',
        'COMMISSION_MANAGEMENT.COMMON.ACTIONS'
      ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((translations) => {
        this.columns.next([
          {
            title: translations['COMMISSION_MANAGEMENT.RULES.CATEGORY'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'category',
            type: 'component',
            data: {
              name: CategoryTagComponent,
              additionData: true,
              aData: {
                keyName: 'category',
                uri: `/admin/commission-management/plans/${this.ownership}/${this.planId}/rules/edit/:id`,
                canRedirect: true
              }
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.RULES.SALES_BASIS'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'sales_basis',
            type: 'component',
            data: {
              name: SalesBasisTagComponent,
              additionData: true
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.RULES.SCOPE_TYPE'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'scope_type',
            type: 'component',
            data: {
              name: ScopeTypeTagComponent,
              additionData: true
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.RULES.SCOPE_NAME'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'scope_name',
            type: 'component',
            data: {
              name: ScopeNameComponent,
              additionData: true
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.RULES.STATUS'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'status',
            type: 'component',
            data: {
              name: StatusTagComponent,
              additionData: true
            }
          },
          {
            title: translations['COMMISSION_MANAGEMENT.COMMON.ACTIONS'],
            classes: 'py-2 white-space-nowrap',
            dataProperty: 'actions',
            type: 'component',
            data: {
              name: ActionsComponent,
              additionData: true,
              aData: ['view', 'edit', 'delete']
            }
          }
        ]);
      });
  }

  loadPlan() {
    this.commissionPlanService.getCommissionPlanByOwnership(
      this.commissionPlanService.getSelectedOwnershipLevel(),
      this.planId,
      this.tenantId,
      this.tenantFranchiseId
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (plan: CommissionPlanResponse) => {
          this.planName = plan.name_pri_lang || plan.name_sec_lang || `Plan ${this.planId}`;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.planName = `Plan ${this.planId}`;
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  loadRules() {
    this.loading = true;
    this.commissionPlanRuleService.getCommissionPlanRules(this.planId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: ListCommissionPlanRulesResponse) => {
        this.rules = response.items || [];
        this.totalRecords = response.total_items;
        
        // Update data grid
        this.tableData.next(this.rules);
        this.totalRecordLength.next({
          totalPage: 1,
          totalItems: this.totalRecords,
          currentPage: 0,
        });
        
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.loading = false;
        this.rules = [];
        this.totalRecords = 0;
        
        // Update data grid with empty data
        this.tableData.next([]);
        this.totalRecordLength.next({
          totalPage: 1,
          totalItems: 0,
          currentPage: 0,
        });
        
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  createRule() {
    this.router.navigate([
      '/admin/commission-management/plans',
      this.ownership,
      this.planId,
      'rules',
      'create'
    ]);
  }

  editRule(rule: CommissionPlanRuleResponse) {
    this.router.navigate([
      '/admin/commission-management/plans',
      this.ownership,
      this.planId,
      'rules',
      'edit',
      rule.id
    ]);
  }

  viewSlabs(rule: CommissionPlanRuleResponse) {
    this.router.navigate([
      '/admin/commission-management/plans',
      this.ownership,
      this.planId,
      'rules',
      rule.id,
      'slabs'
    ]);
  }

  deleteRule(rule: CommissionPlanRuleResponse) {
    this.confirmationService.confirm({
      message: this.translate.instant('COMMISSION_MANAGEMENT.COMMON.CONFIRM_DELETE'),
      header: this.translate.instant('COMMISSION_MANAGEMENT.RULES.DELETE'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.commissionPlanRuleService.deactivateCommissionPlanRule(this.planId, rule.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.RULES.DELETE_SUCCESS'));
              this.loadRules();
            },
            error: (error) => {
              this.toastService.showError(error);
            }
          });
      }
    });
  }

  goBack() {
    this.router.navigate(['/admin/commission-management/plans']);
  }

  onActionEvent(event: any) {
    switch (event.action) {
      case 'view':
        this.viewSlabs(event.columnData);
        break;
      case 'edit':
        this.editRule(event.columnData);
        break;
      case 'delete':
        this.deleteRule(event.columnData);
        break;
    }
  }

  getScopeNameLength(scopeName: string | null | undefined): number {
    return scopeName ? scopeName.length : 0;
  }

  getTruncatedScopeName(scopeName: string | null | undefined): string {
    if (!scopeName) return '';
    return scopeName.length > 50 ? scopeName.substring(0, 50) : scopeName;
  }

  showScopeNamesDialog(scopeName: string | null | undefined, skuName: string | null | undefined) {
    this.dialogScopeName = scopeName || '';
    this.dialogSkuName = skuName || '';
    this.showDialog = true;
    this.changeDetectorRef.detectChanges();
  }

  getScopeNamesList(scopeNames: string | null | undefined): string[] {
    if (!scopeNames) return [];
    return scopeNames.split(',').map(name => name.trim()).filter(name => name.length > 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}